import FormContainer from '@components/FormContainer';
import { ADD_EXPERIENCE_TALENT, DELETE_EXPERIENCE_TALENT, UPDATE_EXPERIENCE_TALENT } from '@gql/experience';

import { useMutation, useQuery } from '@apollo/client';
import AddActionButton from '@components/AddActionButton';
import { AutoCompleteTalentProfessions } from '@components/AutoComplete/AutoCompleteTalentProfessions';
import { AutoCompleteTalentSpecialties } from '@components/AutoComplete/AutoCompleteTalentSpecialties';
import { ButtonSaveType } from '@components/ButtonSaveSplit';
import { confirm } from '@components/ConfirmModal/confirm';
import DashedDivider from '@components/Divider/DashedDivider';
import { FlexRow } from '@components/FlexRow';
import InputDate from '@components/InputDate';
import InputFieldControlled from '@components/InputFieldControlled';
import InputMultiSelect from '@components/InputMultiSelect';
import InputPhone from '@components/InputPhone';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import Text from '@components/Text';
import { GET_OPTIONS_TALENT } from '@gql/options';
import { GET_TALENT } from '@gql/talents';
import { getTalentSpecialtiesQuery } from '@gql/talentSpecialties';
import { checkPropertiesIsHaveValueWithIncludes } from '@helper/validation.utils';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Box, Checkbox, IconButton, Menu, MenuItem, Stack, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import dateUtils, { moment } from '@utils/date.utils';
import { groupBy, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { ExpandableText } from '../ExpandableText';
import { SectionCard } from '../SectionCard';
import { SectionTitle } from '../SectionTitle';
import EmptyExperienceIllustration from "./empty-experience.svg";

type Props = {
    experience?: any;
    onCancel?: () => void;
    onSubmitted?: () => void;
    isEdit?: boolean;
    talentId?: string;
};

// Styled Components
const CheckboxContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
}));

const ContactInfoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        gap: 0,
    },
}));

const ExperienceItemContainer = styled(Box)(({ theme }) => ({
    borderRadius: '1rem',
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'row',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    '&.error': {
        backgroundColor: theme.palette.errorRed?.main ? `${theme.palette.errorRed.main}20` : '#FCEEEE',
    },
    "&:first-child": {
        paddingTop: theme.spacing(0),
    },
    "&:last-child": {
        paddingBottom: theme.spacing(0),
    },
}));


const ActionButtonsContainer = styled(Box)(({ theme }) => ({
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
}));


const ErrorMessageContainer = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.palette.errorRed?.main ? `${theme.palette.errorRed.main}20` : '#FCEEEE',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
}));


const TalentExperienceForm = ({ experience, onCancel, onSubmitted, talentId, ...props }: Props) => {
    const [saveType, setSaveType] = useState<ButtonSaveType>(ButtonSaveType.SAVE_AND_RETURN);

    const [loading, setLoading] = useState(false);
    const [formState, setFormState] = useState<'view' | 'edit'>('edit');
    const location = useLocation();
    const isOnboarding = location.pathname.includes('onboarding');
    const [hide, setHide] = useState(isOnboarding);
    const handleHide = hideState => {
        setHide(hideState);
    };
    const defaultValues = useMemo(() => ({
        title: experience?.title ?? null,
        enterprise: experience?.enterprise?.name ?? null,
        profession: experience?.profession?._id ?? null,
        sector: experience?.sector?._id ?? null,
        specialties: experience?.specialties?.map(item => item._id) ?? null,
        type: experience?.type ?? null,
        city: experience?.city ?? null,
        country: experience?.country ?? null,
        contactNumber: experience?.contactNumber ?? null,
        contactName: experience?.contactName ?? null,
        description: experience?.description ?? null,
        startDate: experience?.startDate ? moment(experience?.startDate).format('YYYY-MM-DD') : null,
        endDate: experience?.endDate ? moment(experience?.endDate).format('YYYY-MM-DD') : null,
    }), [experience]);

    const [presentJob, setPresentJob] = useState(experience?.endDate ? false : true);

    const { control, handleSubmit, getValues, watch, setValue, clearErrors, setError } = useForm({
        mode: 'all',
        defaultValues,
    });

    // snackbar
    const { showSnackbar } = useSnackbar();

    const [save] = useMutation(ADD_EXPERIENCE_TALENT);
    const [update] = useMutation(UPDATE_EXPERIENCE_TALENT);

    const submitHandler = useCallback(async (experienceInput: any) => {
        setLoading(true);
        if (!experience?._id) {
            await save({
                variables: {
                    experienceInput: {
                        ...experienceInput,
                        profession: experienceInput.profession,
                        contactNumber: experienceInput?.contactNumber,
                    },
                    talentId,
                },
                refetchQueries: () => [
                    {
                        query: GET_TALENT,
                        variables: {
                            talentId
                        },
                    },
                ],
            });
            showSnackbar('Expérience ajoutée avec succès', 'success');
        } else {
            await update({
                variables: {
                    _id: experience?._id,
                    experienceInput: {
                        ...experienceInput,
                        profession: experienceInput.profession,
                        contactNumber: experienceInput?.contactNumber,
                    },
                    talentId
                },
                refetchQueries: () => [
                    {
                        query: GET_TALENT,
                        variables: {
                            talentId
                        },
                    },
                ],
            });
            showSnackbar('Expérience modifiée avec succès', 'success');
        }
        if (saveType === ButtonSaveType.SAVE_AND_RETURN) {
            onSubmitted();
        } else {
            Object.keys(defaultValues).forEach(key => {
                setValue(key as keyof typeof defaultValues, null);
            });
        }
        setLoading(false);
    }, [experience?._id, saveType, save, showSnackbar, talentId, update, onSubmitted, defaultValues, setValue]);


    const { data: dataSpecialities } = useQuery(getTalentSpecialtiesQuery);

    const groupedSpecialties = groupBy(dataSpecialities?.specialities ?? [], 'groupName');

    const { data: optionsData } = useQuery(GET_OPTIONS_TALENT);

    const options = useMemo(() => {
        if (!optionsData?.options) return null;
        return optionsData.options;
    }, [optionsData]);

    return (
        <FormContainer
            onBack={() => {
                setHide(false);
                onCancel();
            }}
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            onCancel={onCancel}
            onSubmit={handleSubmit(submitHandler)}
            onButtonSplitChange={type => {
                setSaveType(type);
            }}
            buttonSplitText={{
                [ButtonSaveType.SAVE_AND_RETURN]: 'Enregistrer',
                [ButtonSaveType.SAVE_AND_ADD]: 'Ajouter une nouvelle expérience',
            }}
            submitText={props.isEdit ? 'Enregistrer' : 'Suivant'}
            formTitle={formState === 'view' ? 'Détails de l’expérience' : experience ? 'Modifier mon expérience' : 'Ajouter une expérience'}
            formDescription={'Détaillez votre expérience et rendez votre profil plus visible'}
        >
            <InputFieldControlled
                name="title"
                control={control}
                label="Titre du poste occupé"
                placeholder="Titre"
                required
            />
            <InputFieldControlled
                name="enterprise"
                control={control}
                label="Nom de l’entreprise"
                placeholder="Enterprise"
                required
            />
            <Stack
                flexDirection={{
                    xs: 'column',
                    sm: 'row',
                }}
                gap={2}>
                <InputMultiSelect
                    single
                    control={control}
                    name="sector"
                    label="Secteur d'activité"
                    placeholder="Choisissez votre secteur"
                    options={
                        options?.sectors?.[0]?.children?.map((item: any) => ({
                            label: item.name,
                            value: item.id,
                        })) || []
                    }
                    defaultValue={defaultValues?.sector}
                />
                <AutoCompleteTalentProfessions
                    allowAdd
                    groupedOptions={(options?.groupProfessions ?? []).filter(el => el.name !== 'Fonctions principales')}
                    name="profession"
                    control={control}
                    label="Fonction"
                    placeholder="Sélectionnez la fonction recherchée"
                    multiple={false}
                    defaultValue={defaultValues?.profession}
                    onChange={() => {
                        setValue('specialties', []);
                    }}
                />
            </Stack>
            <AutoCompleteTalentSpecialties
                allowAdd
                label="Compétences requises"
                viewOnly={formState === 'view'}
                placeholder={'Sélectionnez les compétences maîtrisées'}
                control={control}
                name="specialties"
                groupedOptions={Object.keys(groupedSpecialties).map(groupName => ({
                    name: groupName,
                    children: groupedSpecialties[groupName].map(item => ({
                        id: item._id,
                        name: item.name,
                        value: item._id,
                    })),
                }))}
            />
            <InputMultiSelect
                name="type"
                control={control}
                label="Contrat"
                placeholder="Contrat"
                required
                single
                groupedOptions={options?.preferences?.map((item: any) => {
                    return {
                        label: item.name,
                        options: item.children?.map((child: any) => ({
                            ...child,
                            label: child.name,
                            value: child.id,
                        })),
                    };
                })}
            />
            <FlexRow gap={2}>
                <InputFieldControlled name="city" control={control} label="Ville" placeholder="Ville" required />
                <InputFieldControlled name="country" control={control} label="Pays" placeholder="Pays" required />
            </FlexRow>
            <FlexRow gap={2} alignItems={'flex-start'}>
                <InputDate
                    name="startDate"
                    control={control}
                    label="Date de début"
                    placeholder="Date de début"
                    onChange={(val: string) => {
                        if (
                            moment(val).isAfter(moment(getValues('endDate'))) ||
                            moment(val).isSame(moment(getValues('endDate')))
                        ) {
                            setValue('endDate', moment(val).add(1, 'day').format('YYYY-MM-DD'));
                        }
                    }}
                    required
                />
                <InputDate
                    name="endDate"
                    control={control}
                    minDate={watch('startDate') ? moment(watch('startDate')).add('1', 'day') : undefined}
                    label="Date de fin"
                    placeholder="Date de fin"
                    onChange={(val: string) => {
                        setPresentJob(val ? false : true);
                    }}
                    required={!presentJob}
                />
            </FlexRow>
            {/* MUI CHECKBOX LIFETIME */}
            <CheckboxContainer component="label">
                <Checkbox
                    id="checkbox-lifetime"
                    checked={presentJob}
                    onChange={e => {
                        if (e.target.checked) {
                            setValue('endDate', null);
                            // remove error from endDate required
                            clearErrors('endDate');
                        } else {
                            // set error to endDate required
                            setError('endDate', {
                                type: 'required',
                                message: ' La date de fin est obligatoire si vous n’êtes plus en poste',
                            });
                        }
                        setPresentJob(!!e.target.checked);
                    }}
                    inputProps={{ 'aria-label': 'controlled' }}
                />
                <Text darkGray size={16} error={isEmpty(watch('endDate')) && !presentJob}>
                    Actuellement en poste dans l’entreprise
                </Text>
            </CheckboxContainer>

            <InputFieldControlled
                name="description"
                control={control}
                label="Description"
                placeholder="Description"
                multiline
                maxLength={1000}
                numberOfLines={5}
            />
            <ContactInfoContainer>
                <InputFieldControlled name="contactName" control={control} label="Nom du contact" placeholder="" />
                <InputPhone
                    name="contactNumber"
                    hide={hide}
                    handleHide={handleHide}
                    control={control}
                    label="Numéro de contact"
                    defaultValue={experience?.contactNumber}
                />
            </ContactInfoContainer>
        </FormContainer>
    );
};

export const TalentExperienceItem = ({
    divider,
    experience,
    onClick,
    onDelete
}: {
    divider: boolean;
    experience: any;
    onClick?: () => void;
    onDelete?: () => void;
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        onClick?.();
    };

    const handleDelete = () => {
        handleMenuClose();
        onDelete?.();
    };

    const {
        title,
        description,
        enterprise,
        city,
        country,
        specialties,
    } = experience;

    const hasError = !checkTalentExperienceValue(experience).status;
    const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md')); // Adjust breakpoint as needed


    const location = [city, country].filter(Boolean).join(', ');

    return (
        <>
            <ExperienceItemContainer
                className={hasError ? 'error' : ''}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: '16px',
                        width: '100%',
                    }}>
                    {/* Logo */}
                    <Box
                        sx={{
                            width: '46px',
                            height: '46px',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            overflow: 'hidden',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                        {/* Logo placeholder - could be added from enterprise data later */}
                        <img
                            src={experience?.enterprise?.logo || '/icons/default-enterprise-logo.jpg'}
                            width={isMobile ? 32 : 46}
                            height={isMobile ? 32 : 46}
                            alt={experience?.enterprise?.name || 'Logo de l\'entreprise'}
                            style={{ borderRadius: '8px' }}
                        />
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', gap: '4px', width: '100%' }}>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    sx={{ fontSize: '18px', lineHeight: 'normal' }}>
                                    {title || 'Titre du poste'}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {location && (
                                        <>
                                            <Typography variant="body2" color="#cbcbcb">
                                                {location}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    width: '6px',
                                                    height: '6px',
                                                    borderRadius: '50%',
                                                    background: '#cbcbcb',
                                                }}
                                            />
                                        </>
                                    )}
                                    <Typography variant="body2" fontWeight={500} color="#cbcbcb">
                                        {enterprise?.name || 'Nom de l\'entreprise'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {moment(experience.startDate).format('MMM YYYY')}
                                {' - '}
                                {experience.endDate
                                    ? moment(experience.endDate).format('MMM YYYY')
                                    : "Aujourd'hui"}{' '}
                                <Text gray size={isMobile ? 10 : 14}>
                                    ({' '}
                                    {experience.numberOfMonths
                                        ? dateUtils.durationMonthYearByMonths(experience.numberOfMonths)
                                        : dateUtils.durationMonthYear(experience.startDate, experience.endDate)}
                                    )
                                </Text>
                            </Typography>
                        </Box>

                        {/* Description */}
                        {description && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <ExpandableText content={description} maxLine={1} />
                            </Box>
                        )}

                        {/* Skills */}
                        {specialties && specialties.length > 0 && (
                            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                {specialties.map((specialty, idx) => (
                                    <Box
                                        key={specialty._id || idx}
                                        sx={{
                                            background: 'rgba(60, 45, 225, 0.30)',
                                            padding: '8px 12px',
                                            borderRadius: '16px',
                                        }}>
                                        <Typography variant="body2" fontSize="14px" fontWeight={500} color="#fff">
                                            {specialty.name || 'Spécialité'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>
                {onClick && onDelete && (
                    <ActionButtonsContainer>
                        <IconButton
                            aria-label="more"
                            aria-controls={open ? 'action-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={open ? 'true' : undefined}
                            onClick={handleMenuOpen}
                            size="small"
                            sx={{
                                borderRadius: '4px',
                                color: (theme) => theme.palette.text.secondary,
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.action.hover,
                                },
                            }}
                        >
                            <MoreVertIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                        <Menu
                            id="action-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleMenuClose}
                            MenuListProps={{
                                'aria-labelledby': 'action-button',
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem onClick={handleEdit}>
                                <i
                                    className="fi fi-rr-edit"
                                    style={{
                                        marginRight: '8px',
                                        fontSize: isMobile ? 14 : 16
                                    }}
                                />
                                Modifier
                            </MenuItem>
                            <MenuItem onClick={handleDelete}>
                                <i
                                    className="fi fi-rr-trash"
                                    style={{
                                        marginRight: '8px',
                                        fontSize: isMobile ? 14 : 16,
                                        color: '#d32f2f'
                                    }}
                                />
                                Supprimer
                            </MenuItem>
                        </Menu>
                    </ActionButtonsContainer>
                )}
            </ExperienceItemContainer>
            {divider && <DashedDivider />}
        </>
    );
};

export const TalentExperienceEmpty = () => {
    const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md')); // Adjust breakpoint as needed
    return (
        <Stack p={3} gap={3} justifyContent={'center'} alignItems={'center'}>
            <img src={EmptyExperienceIllustration} alt="Aucune expérience trouvée" />
            <Stack maxWidth={435} gap={1}>
                <Typography textAlign="center">
                    Dites-nous quel est votre parcours
                </Typography>
                <Typography textAlign="center" color="textSecondary" fontSize={isMobile ? 12 : 16}>
                    Faites passer votre profil au niveau supérieur en incluant votre expérience professionnelle.
                </Typography>
            </Stack>
        </Stack>
    );
};

// Validation functions to match individual forms
export const checkTalentExperienceValue = (experience: any) => {
    // if it's not current endDate is required
    if (!experience.current) {
        return checkPropertiesIsHaveValueWithIncludes(experience, [
            'title',
            'enterprise',
            'type',
            'city',
            'country',
            'startDate',
            'endDate',
        ]);
    }
    return checkPropertiesIsHaveValueWithIncludes(experience, [
        'title',
        'enterprise',
        'type',
        'city',
        'country',
        'startDate',
    ]);
};

const TalentExperienceList = ({
    talent,
    onSuccess = () => { },
    onBack = () => { },
    isOnboarding = false
}: {
    talent: any;
    onSuccess?: () => void;
    onBack?: () => void;
    isOnboarding?: boolean;
}) => {
    const [selectedTalentExperience, setSelectedTalentExperience] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [deleteTalentExperience] = useMutation(DELETE_EXPERIENCE_TALENT, {});

    useEffect(() => {
        setHasError(
            talent?.experiences?.some((experience: any) => {
                return !checkTalentExperienceValue(experience).status;
            }),
        );
    }, [talent?.experiences]);

    const handleDelete = (_id: string) => {
        (async () => {
            if (await confirm({ confirmation: 'Êtes-vous sûr de vouloir supprimer cette expérience?' })) {
                deleteTalentExperience({
                    variables: {
                        _id,
                        talentId: talent?._id
                    },
                    refetchQueries: () => [
                        {
                            query: GET_TALENT,
                            variables: {
                                talentId: talent?._id
                            }
                        },
                    ],
                });
            } else {
                console.log('cancel cancelled');
            }
        })();
    };

    if (openForm) {
        return (
            <TalentExperienceForm
                experience={selectedTalentExperience}
                onCancel={() => {
                    setOpenForm(false);
                    setSelectedTalentExperience(null);
                }}
                onSubmitted={() => {
                    setOpenForm(false);
                    setSelectedTalentExperience(null);
                    onSuccess();
                }}
                talentId={talent?._id}
                isEdit={!!selectedTalentExperience}
            />
        );
    }

    return (
        <SectionCard sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <FlexRow alignItems="center" justifyContent="space-between">
                <SectionTitle title="Expérience" withInfo />
                <AddActionButton
                    onClick={() => {
                        setOpenForm(true);
                        setSelectedTalentExperience(null);
                    }}
                    />
            </FlexRow>
            {hasError && (
                <ErrorMessageContainer>
                    <Typography
                        sx={{
                            color: theme => theme.palette.mode === 'dark' ? '#E45858' : '#E45858',
                            textAlign: 'center'
                        }}
                    >
                        Veuillez compléter les informations manquantes ou supprimer la formation encadrée de
                        rouge.
                    </Typography>
                </ErrorMessageContainer>
            )}
            {/* EDUCATION ITEM */}
            {talent?.experiences?.map((experience: any, index: number) => (
                <React.Fragment key={experience?._id}>
                    <TalentExperienceItem
                        onClick={() => {
                            setOpenForm(true);
                            setSelectedTalentExperience(experience);
                        }}
                        onDelete={() => handleDelete(experience?._id)}
                        experience={experience}
                        divider={index !== talent?.experiences?.length - 1}
                    />
                </React.Fragment>
            ))}
            {/* EMPTHY STATE */}
            {(talent?.experiences?.length === 0 || !talent) && <TalentExperienceEmpty />}
        </SectionCard>

    );
};

export default TalentExperienceList;
